import React from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  Navigate,
  Outlet,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";

import Select from "react-select";

import DebouncedInput from "@components/form/DebouncedInput";
import Label from "@components/form/Label";
import AdministrativeApprovalModal from "@components/modal/AdministrativeApprovalModal";
import ApprovalTag from "@components/shared/ApprovalTag";
import Loading from "@components/shared/Loading";
import Pager from "@components/zenith/ui/Pager";
import PageHeading from "@components/zenith/ui/PageHeading";
import Table, { type Column } from "@components/zenith/ui/Table";
import Tabs from "@components/zenith/ui/Tabs";
import { useAppContext } from "@context/AppContext";
import { useAllAdministrativeApprovals } from "@hooks/query/zenith/useAllAdministrativeApprovals";
import { getAllSubscriberAdministrativeApprovals } from "@services/zenith/administrativeApprovals";
import { downloadAsCSV } from "@utils/downloadAsCSV";
import { toastError, toastSuccess } from "@utils/toast";
import {
  AdministrativeApprovalNumericStatus as Status,
  AdministrativeApprovalNumericType as Type,
} from "@services/administrativeApprovals";
import {
  getActionTimestamp,
  getStatusAndText,
} from "@utils/administrativeApproval";
import { formatVolume } from "@utils/formatVolume";

type Approval = Record<string, any>;

const PAGE_SIZE = 10;

const STATUSES = [
  Status.Pending,
  Status.UnderReview,
  Status.Approved,
  Status.Declined,
  Status.Abandoned,
  Status.InDraft,
];

const firstRight = (approval: Approval) => approval.extractionRights?.[0];

// Each tab is one approval type, with the columns that type fills in; the
// status, date and actions columns follow on every tab.
const useApprovalTabs = () => {
  const { t } = useTranslation();
  const col = (
    key: string,
    label: string,
    render: (approval: Approval) => React.ReactNode,
  ): Column<Approval> => ({ key, label, render: a => render(a) ?? "-" });

  const from = col(
    "from",
    t("approval.seasonal_water_assignments.from_subscriber"),
    a => a.seller?.name,
  );
  const to = col(
    "to",
    t("approval.seasonal_water_assignments.to_subscriber"),
    a => a.buyer?.name,
  );
  const zone = col(
    "zone",
    t("common.level0wrs"),
    a => firstRight(a)?.level0Resource?.identifier,
  );
  const rights = col(
    "rights",
    t("common.extraction_rights"),
    a => a.extractionRights?.map((r: Approval) => r.name).join(", ") || null,
  );
  const right = col(
    "right",
    t("common.extraction_right"),
    a => firstRight(a)?.name,
  );

  return {
    seasonals: {
      label: t("zenith.approval.tabs.seasonals"),
      type: Type.SWT,
      columns: [
        from,
        to,
        zone,
        rights,
        col("volume", t("common.volume"), a =>
          a.volume ? formatVolume(+a.volume) : null,
        ),
      ],
    },
    permanents: {
      label: t("zenith.approval.tabs.permanents"),
      type: Type.PT,
      columns: [from, to, zone, rights],
    },
    forward_draws: {
      label: t("zenith.approval.tabs.forward_draws"),
      type: Type.FD,
      columns: [right],
    },
    stock_and_domestics: {
      label: t("zenith.approval.tabs.stock_and_domestics"),
      type: Type.SD,
      columns: [zone, rights],
    },
    admin_transfers: {
      label: t("zenith.approval.tabs.admin_transfers"),
      type: Type.AT,
      columns: [
        right,
        col(
          "offtake",
          t("extraction_point.link.offtake_id"),
          a => a.extractionPoint?.name,
        ),
        { ...zone, label: t("extraction_point.link.entitlement_zone") },
        col(
          "point_zone",
          t("extraction_point.link.point_zone"),
          a => a.extractionPoint?.level0WRS?.identifier,
        ),
      ],
    },
  };
};

type TabName = keyof ReturnType<typeof useApprovalTabs>;

type StatusContext = {
  statuses: Partial<Record<TabName, Status>>;
  setStatus: (tab: TabName, status?: Status) => void;
};

/** Trades & Approvals: the customer's approval requests, a tab per type. */
const Approvals: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const tabs = useApprovalTabs();
  const title = t("zenith.navigation.approvals");
  // Held here, above the tabs, so each tab's status filter survives a visit to another tab.
  const [statuses, setStatuses] = React.useState<StatusContext["statuses"]>({});
  const context: StatusContext = {
    statuses,
    setStatus: (tab, status) =>
      setStatuses(prev => {
        const { [tab]: _removed, ...rest } = prev;
        return status ? { ...rest, [tab]: status } : rest;
      }),
  };

  return (
    <div className="space-y-5">
      <PageHeading title={title} sub={t("zenith.approval.sub")} />
      <div className="rounded-lg border border-gray-200 bg-white">
        <Tabs
          label={title}
          tabs={Object.entries(tabs).map(([to, { label }]) => ({
            label,
            to,
          }))}
        />
        <Outlet context={context} />
      </div>
    </div>
  );
};

/** The selected tab's approvals. Keyed by tab, so its search and page start afresh. */
export const ApprovalList: React.FunctionComponent = () => {
  const { tab } = useParams();
  const tabs = useApprovalTabs();
  if (!tab || !Object.keys(tabs).includes(tab)) {
    return <Navigate to="../seasonals" replace />;
  }
  return <TabApprovals key={tab} tab={tab as TabName} />;
};

/** One tab's approvals, searched and filtered by status on the server a page at a time; View opens the shared detail modal. */
const TabApprovals: React.FunctionComponent<{ tab: TabName }> = ({ tab }) => {
  const { t } = useTranslation();
  const { isBetweenPeriods } = useAppContext();
  const tabs = useApprovalTabs();
  const [searchParams, setSearchParams] = useSearchParams();
  const { statuses, setStatus: saveStatus } = useOutletContext<StatusContext>();
  const status = statuses[tab];
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [exporting, setExporting] = React.useState(false);
  const current = tabs[tab];
  const filters = {
    types: [current.type],
    status,
    search: search || undefined,
  };
  const { data, isLoading } = useAllAdministrativeApprovals({
    params: { ...filters, page, limit: PAGE_SIZE },
    keepPreviousData: true,
  });

  const columns: Column<Approval>[] = [
    ...current.columns,
    {
      key: "status",
      label: t("common.status"),
      render: a => (
        <ApprovalTag status={a.status} isAccounted={Boolean(a.accountedAt)} />
      ),
    },
    {
      key: "date",
      label: t("common.actioned_at"),
      render: getActionTimestamp,
    },
    {
      key: "actions",
      label: <span className="sr-only">{t("common.actions")}</span>,
      render: a => (
        <div className="flex justify-end gap-2 whitespace-nowrap">
          {a.status === Status.UnderReview ? (
            <Link
              to={`/zenith/workflows/response_approval_info_request?selectedId=${a.id}`}
              className="btn-primary py-1.5 text-xs font-semibold"
            >
              {t("common.response")}
            </Link>
          ) : null}
          <button
            type="button"
            className="btn-default py-1.5 text-xs font-semibold"
            onClick={() => setSearchParams({ id: a.id })}
          >
            {t("common.view")}
          </button>
        </div>
      ),
    },
  ];
  const approvalId = searchParams.get("id");
  const statusOptions = STATUSES.map(value => ({
    value,
    label: getStatusAndText(value).text,
  }));

  // Every row the search and status match, not just the page on screen.
  const exportAll = async () => {
    setExporting(true);
    try {
      const { administrativeApprovals } =
        await getAllSubscriberAdministrativeApprovals({
          ...filters,
          limit: -1,
        });
      downloadAsCSV({
        fileName: tab,
        data: administrativeApprovals.map((a: Approval) => ({
          ...Object.fromEntries(
            current.columns.map(column => [column.label, column.render(a)]),
          ),
          [t("common.status") as string]: getStatusAndText(a.status).text,
          [t("common.actioned_at") as string]: getActionTimestamp(a),
        })),
      });
      toastSuccess(t("shared.table.toast.export.success"));
    } catch (error: any) {
      toastError(
        t("shared.table.toast.export.failed", {
          error: error?.response?.data?.message ?? error?.message,
        }),
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4 p-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="grow basis-64">
          <Label htmlFor="approval_search">{t("zenith.approval.search")}</Label>
          <DebouncedInput
            id="approval_search"
            type="search"
            value={search}
            debounce={300}
            // DebouncedInput also reports its initial value; only a change restarts the paging.
            onChange={value => {
              if (String(value) === search) return;
              setSearch(String(value));
              setPage(1);
            }}
          />
        </div>
        <div className="w-56">
          <Label htmlFor="approval_status">{t("common.status")}</Label>
          <Select
            inputId="approval_status"
            options={statusOptions}
            value={statusOptions.find(o => o.value === status) ?? null}
            placeholder={t("zenith.approval.all_statuses")}
            onChange={option => {
              saveStatus(tab, option?.value);
              setPage(1);
            }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 20 }) }}
            isClearable
          />
        </div>
        <button
          type="button"
          className="btn-default text-sm font-semibold"
          disabled={exporting}
          onClick={exportAll}
        >
          {t("common.export")}
        </button>
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <Table
          aria-label={current.label}
          columns={columns}
          rows={data?.administrativeApprovals ?? []}
          rowKey={a => a.id}
          // Approvals list only for the active period, so between periods there are none to show.
          empty={
            isBetweenPeriods
              ? t("zenith.dashboard.no_active_period")
              : t("zenith.approval.empty")
          }
        />
      )}
      <Pager
        page={page}
        pageCount={Math.max(data?.meta?.lastPage ?? 1, 1)}
        onPage={setPage}
      />
      {approvalId ? <AdministrativeApprovalModal id={approvalId} /> : null}
    </div>
  );
};

export default Approvals;
